import type { Table2DComponentSpec, Table2DDataSpec } from "../Processing/types";
import { measureTextPx as measureSharedTextPx } from "../PDF/textMeasure";
import { formatToDecimals, inferDecimalsFromLabels } from "../Processing/decimals";

// Shared normalization for the 2D table: turns a Table2DDataSpec + spec into a
// flat grid (column headers, rows of aligned cells) with pinned Best/Avg/Total
// placed as extra columns or extra rows by the primary-axis rule (see
// table2dPinnedAsRows). Consumed by the heatmap component, the plain table,
// and the PDF exporter so the pinned-placement rules live in exactly one place.

const TIME_DIMS = new Set(["day", "week", "month"]);
export const DEFAULT_HEX = "#CCCCCC";

// ── Column sizing, shared by the live plain table and the PDF exporter ──
// Header text is measured in the platform's actual font so PDF column widths (and
// thus the truncation point) match the live table exactly; estimation can't match
// both name columns and date columns at once. A cell label is a number for most
// metrics and fits in the minimum width. An options-type wellness answer ("Between
// 6 and 8 hrs") is text, so the widest label of a column also sets its width.

const CELL_PAD_PX = 16; // antd/heatmap cell horizontal padding (~8px each side)
/** Narrowest column the PDF exporter draws. */
export const COL_MIN_PX = 56;
/** Narrowest column the live plain table draws. */
export const LIVE_COL_MIN_PX = 72;
/** A plain column stops growing here; a longer answer wraps onto a second line. */
export const COL_MAX_PX = 220;

// Header and cell labels are measured in the platform font. The measurer lives in
// PDF/textMeasure: a canvas in the browser, font metrics in the headless renderer.
const HEADER_FONT = "600 14px";
export function measureTextPx(text: string): number {
  return measureSharedTextPx(text, HEADER_FONT);
}

const longestWordPx = (label: string) =>
  Math.max(0, ...label.split(/\s+/).map((w) => measureTextPx(w)));

/** Width of the widest label, measured once on the longest string. */
export function widestLabelPx(labels: Iterable<string>): number {
  let widest = "";
  for (const label of labels) if (label.length > widest.length) widest = label;
  return widest ? measureTextPx(widest) : 0;
}

/**
 * PDF column width in px matching how the platform sizes it. Plain headers wrap,
 * so they size to the longest word; heatmap headers are single-line, so the full
 * label. A wider cell label wins. Plain columns cap at COL_MAX_PX and wrap the
 * label instead; heatmap cells never wrap (they match the live nowrap cells).
 */
export function measuredColWidth(headerLabel: string, plain: boolean, cellLabels: Iterable<string> = []): number {
  const headerPx = plain ? longestWordPx(headerLabel) : measureTextPx(headerLabel);
  const width = Math.max(COL_MIN_PX, Math.max(headerPx, widestLabelPx(cellLabels)) + CELL_PAD_PX);
  return plain ? Math.min(COL_MAX_PX, width) : width;
}

/** Live plain-table column width: the historical fixed width, grown for a text label. */
export function liveColWidth(cellLabels: Iterable<string>): number {
  return Math.min(COL_MAX_PX, Math.max(LIVE_COL_MIN_PX, widestLabelPx(cellLabels) + CELL_PAD_PX));
}

/** Lines the widest label of a column needs at the given width (1 for a number). Capped at 3. */
export function labelLines(cellLabels: Iterable<string>, widthPx: number): number {
  const px = widestLabelPx(cellLabels);
  if (px === 0) return 1;
  return Math.min(3, Math.max(1, Math.ceil(px / Math.max(1, widthPx - CELL_PAD_PX))));
}

/** The non-empty labels of one grid column, top to bottom. */
export function gridColLabels(grid: Table2DGrid, ci: number): string[] {
  return grid.rows.map((r) => r.cells[ci]?.label ?? "").filter((label) => label !== "");
}

/**
 * Pinned Best/Avg/Total are keyed by a pair's PRIMARY axis: the entity of an
 * entity×time pair, the non-drill entity of a player/position×drill pair, the
 * week of a week×weekday pair. They render as extra rows when that axis is on
 * the columns, extra columns when it is on the rows. Only meaningful for
 * non-metric pairs (the backend sends no pinned data otherwise).
 */
export function table2dPinnedAsRows(
  row: Table2DComponentSpec["rowDimension"],
  col: Table2DComponentSpec["colDimension"],
): boolean {
  const primary =
    row === "weekday" || col === "weekday"
      ? "week"
      : !TIME_DIMS.has(row) && !TIME_DIMS.has(col)
        ? (row === "drill" ? col : row)
        : TIME_DIMS.has(row)
          ? col
          : row;
  return primary === col;
}

/** Heatmap cell backgrounds for pinned (Best/Avg/Total) and null (no-data) cells. */
export const HEATMAP_PINNED_BG = "#eef1f5";
export const HEATMAP_NULL_BG = "#cccccc";

/** Readable dark text color (hex, ~rgba(0,0,0,0.85)). */
export const TEXT_DARK = "#262626";

/** Resolve a heatmap cell's background + readable text color (shared by the
 *  live component and the PDF exporter so they render identically). */
export function heatmapCellColors(
  value: number | null,
  hex: string | undefined,
  isPinned: boolean,
): { bg: string; color: string } {
  // Hex (not rgba) so the values are reused verbatim by the react-pdf exporter.
  if (value == null) return { bg: HEATMAP_NULL_BG, color: TEXT_DARK };
  if (isPinned) return { bg: HEATMAP_PINNED_BG, color: TEXT_DARK };
  const fill = hex ?? DEFAULT_HEX;
  return { bg: fill, color: readableTextColor(fill) };
}

export interface Table2DGridCell {
  value: number | null;
  /** Per-cell heatmap color from the backend; undefined for pinned/neutral cells. */
  hex?: string;
  label: string;
}

export interface Table2DGridCol {
  key: string;
  label: string;
  pinned: boolean;
}

export interface Table2DGridRow {
  key: string;
  label: string;
  pinned: boolean;
  /** Aligned 1:1 with `cols`. */
  cells: Table2DGridCell[];
}

export interface Table2DGrid {
  cols: Table2DGridCol[];
  rows: Table2DGridRow[];
}

const mkCell = (value: number | null, hex?: string, label?: string): Table2DGridCell => ({
  value,
  hex,
  label: value == null ? "" : (label ?? String(value)),
});

export function buildTable2DGrid(
  table2d: Table2DDataSpec,
  spec: Table2DComponentSpec,
): Table2DGrid {
  const { row_keys, row_labels, col_keys, col_labels, cells, pinned_best, pinned_avg, pinned_total } = table2d;

  const pinnedGoAsRows = table2dPinnedAsRows(spec.rowDimension, spec.colDimension);
  const isMetricDim = spec.rowDimension === "metric" || spec.colDimension === "metric";
  const hasPinned =
    !isMetricDim && (spec.pinnedCols.best || spec.pinnedCols.avg || spec.pinnedCols.total);

  const rowKeySet = new Set(row_keys);
  const colKeySet = new Set(col_keys);
  const cellMap: Record<string, Record<string, { value: number | null; hex?: string; label?: string }>> = {};
  for (const cell of cells) {
    if (!rowKeySet.has(cell.row_key) || !colKeySet.has(cell.col_key)) {
      console.warn(`buildTable2DGrid: skipping cell with unrecognised key (row="${cell.row_key}", col="${cell.col_key}")`);
      continue;
    }
    const hex = cell.hex && cell.hex.toUpperCase() !== DEFAULT_HEX ? cell.hex : undefined;
    (cellMap[cell.row_key] ??= {})[cell.col_key] = { value: cell.value, hex, label: cell.value_label ?? undefined };
  }

  // The backend labels each cell to the metric's decimal count. The pinned
  // Best/Avg/Sum arrive as plain numbers, so format them to the same count.
  const pinDecimals = inferDecimalsFromLabels(cells.map((cell) => cell.value_label));
  const pinLabel = (v: number | null): string | undefined =>
    v == null || pinDecimals == null ? undefined : formatToDecimals(v, pinDecimals);

  type Pin = { key: string; label: string; values: Record<string, number> };
  const pins: Pin[] = [];
  if (hasPinned) {
    if (spec.pinnedCols.best && pinned_best) pins.push({ key: "__best", label: "Best", values: pinned_best });
    if (spec.pinnedCols.avg && pinned_avg) pins.push({ key: "__avg", label: "Avg", values: pinned_avg });
    if (spec.pinnedCols.total && pinned_total) pins.push({ key: "__total", label: "Sum", values: pinned_total });
  }

  // Pinned columns are prepended when the primary axis is on rows; otherwise they become rows.
  const pinnedAsCols = hasPinned && !pinnedGoAsRows;
  const pinnedCols: Table2DGridCol[] = pinnedAsCols ? pins.map((p) => ({ key: p.key, label: p.label, pinned: true })) : [];
  const dataCols: Table2DGridCol[] = col_keys.map((k) => ({ key: k, label: col_labels[k] ?? k, pinned: false }));
  const cols = [...pinnedCols, ...dataCols];

  const dataRows: Table2DGridRow[] = row_keys.map((rk) => {
    const rowCells: Table2DGridCell[] = [];
    for (const p of pins) {
      if (!pinnedAsCols) break;
      rowCells.push(mkCell(p.values[rk] ?? null, undefined, pinLabel(p.values[rk] ?? null)));
    }
    for (const ck of col_keys) {
      const entry = cellMap[rk]?.[ck];
      rowCells.push(mkCell(entry?.value ?? null, entry?.hex, entry?.label));
    }
    return { key: rk, label: row_labels[rk] ?? rk, pinned: false, cells: rowCells };
  });

  // Pinned rows are prepended when the primary axis is on cols (keyed by col_key).
  const pinnedRows: Table2DGridRow[] =
    hasPinned && pinnedGoAsRows
      ? pins.map((p) => ({
          key: p.key,
          label: p.label,
          pinned: true,
          cells: col_keys.map((ck) => mkCell(p.values[ck] ?? null, undefined, pinLabel(p.values[ck] ?? null))),
        }))
      : [];

  return { cols, rows: [...pinnedRows, ...dataRows] };
}

/** Choose a readable text color (dark/light) for a given cell background hex. */
export function readableTextColor(hex?: string): string {
  if (!hex) return TEXT_DARK;
  const m = hex.replace("#", "");
  if (m.length !== 6) return TEXT_DARK;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return TEXT_DARK;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? TEXT_DARK : "#ffffff";
}
